import styled, { css, keyframes } from "styled-components";
import { Link } from "wouter";
import { SPACING, QUERIES, ANIMATION } from "../../utils/constants";
import { BaseButton } from "../Button";

const dropdownIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-5px) scale(0.975);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// ─── Desktop Navbar ───────────────────────────────────────────────────────────

export const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 52px;
  background: var(--color-nav-background);
  box-shadow: 0 1px 0 var(--color-nav-divider);
  z-index: 1000;
  display: none;

  @media ${QUERIES.tabletLargeAndUp} {
    display: flex;
    align-items: center;
  }
`;

export const NavbarInner = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 ${SPACING.lg}px;
`;

export const BrandSection = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm + SPACING.xxs}px;
  text-decoration: none;
  flex-shrink: 0;

  &:hover {
    opacity: 0.88;
  }

  &:focus-visible {
    outline: 2px solid var(--color-nav-border-focus);
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

export const BrandMark = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: var(--color-nav-icon-background);
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--color-white);
  flex-shrink: 0;
  letter-spacing: -0.02em;
`;

export const BrandText = styled.span`
  font-size: var(--text-base);
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--color-nav-header-color);
  white-space: nowrap;
`;

export const NavList = styled.ul`
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0 0 0 ${SPACING.lg + SPACING.xs}px;
  padding: 0;
  gap: ${SPACING.xxs}px;

  @media (max-width: 1000px) {
    margin-left: ${SPACING.md}px;
  }
`;

export const NavLinkItem = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  height: 52px;
  padding: 0 ${SPACING.sm + SPACING.xs + SPACING.xxs}px;
  font-size: var(--text-base);
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  color: ${({ $active }) =>
    $active ? "var(--color-nav-active-accent)" : "var(--color-nav-item-text)"};
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  border-bottom: 2px solid
    ${({ $active }) =>
      $active ? "var(--color-nav-active-underline)" : "transparent"};
  transition:
    background ${ANIMATION.duration.fast} ease-out,
    color ${ANIMATION.duration.fast} ease-out;

  &:hover:not([aria-current="page"]) {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--color-nav-border-focus);
    outline-offset: -2px;
  }

  @media (max-width: 1000px) {
    padding: 0 ${SPACING.sm + SPACING.xxs}px;
  }
`;

export const AvatarWrapper = styled.div`
  position: relative;
  margin-left: auto;
  flex-shrink: 0;
`;

export const AvatarButton = styled(BaseButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${SPACING.xxs}px;
  border-radius: 50%;
  transition: opacity ${ANIMATION.duration.fast} ease-out;

  &:hover {
    opacity: 0.82;
  }

  &:focus-visible {
    box-shadow: none;
    outline: 2px solid var(--color-nav-border-focus);
    outline-offset: 2px;
  }
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 176px;
  background: var(--color-background-2);
  border: 1px solid var(--color-nav-divider);
  border-radius: 12px;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.18),
    0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 1001;
  animation: ${dropdownIn} 130ms cubic-bezier(0.16, 1, 0.3, 1);
`;

export const DropdownItem = styled(BaseButton)`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm + SPACING.xxs}px;
  width: 100%;
  height: 44px;
  padding: 0 ${SPACING.sm + SPACING.xs + SPACING.xxs}px;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  font-size: var(--text-base);
  font-weight: 500;
  font-family: inherit;
  color: var(--color-text-default);
  transition:
    background ${ANIMATION.duration.fast} ease-out,
    color ${ANIMATION.duration.fast} ease-out;

  svg {
    opacity: 0.5;
    flex-shrink: 0;
    transition: opacity ${ANIMATION.duration.fast} ease-out;
  }

  &:hover {
    background: var(--color-nav-item-hover);

    svg {
      opacity: 0.8;
    }
  }

  &:active {
    background: var(--color-nav-item-hover-active);
  }

  /* Inset ring: the surrounding menu clips an outer one. */
  &:focus-visible {
    outline: 2px solid var(--color-nav-border-focus);
    outline-offset: -2px;
    box-shadow: none;
  }
`;

export const DropdownItemDestructive = styled(DropdownItem)`
  color: var(--color-error);

  svg {
    opacity: 0.65;
  }

  &:hover {
    background: var(--color-error-background);

    svg {
      opacity: 1;
    }
  }

  &:active {
    background: var(--color-error-background);
    filter: brightness(0.92);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

// ─── Mobile Bottom Nav ────────────────────────────────────────────────────────

const bottomNavTabStyles = css<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  padding-top: ${SPACING.xs}px;
  height: 52px;
  gap: ${SPACING.xs}px;
  color: ${({ $active }) =>
    $active ? "var(--color-nav-bottom-accent)" : "var(--color-text-secondary)"};
  transition: color ${ANIMATION.duration.fast} ease-out;

  &:active {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid var(--color-nav-bottom-accent);
    outline-offset: -2px;
    border-radius: 4px;
    box-shadow: none;
  }
`;

export const BottomNavContainer = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: calc(64px + env(safe-area-inset-bottom, 0px));
  background: var(--color-background-2);
  border-top: 1px solid var(--color-card-border);
  display: flex;
  align-items: flex-start;
  padding-top: ${SPACING.xs + SPACING.xxs}px;
  z-index: 1000;

  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const BottomNavItem = styled(Link)<{ $active: boolean }>`
  ${bottomNavTabStyles}
  text-decoration: none;
  position: relative;
`;

export const BottomNavMoreButton = styled(BaseButton)<{ $active: boolean }>`
  ${bottomNavTabStyles}
  background: none;
  border: none;
  cursor: pointer;
`;

export const BottomNavIconPill = styled.span<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 28px;
  border-radius: 14px;
  background: ${({ $active }) =>
    $active ? "var(--color-nav-bottom-accent-bg)" : "transparent"};
  transition:
    background ${ANIMATION.duration.fast} ease-out,
    transform ${ANIMATION.duration.fast} ease-out;

  ${BottomNavItem}:active &,
  ${BottomNavMoreButton}:active & {
    transform: scale(0.92);
  }
`;

export const BottomNavIcon = styled.span`
  font-size: var(--text-lg);
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BottomNavLabel = styled.span`
  font-size: var(--text-xs);
  letter-spacing: 0.02em;
  font-weight: 500;
  line-height: 1;
`;

// ─── Bottom Drawer ────────────────────────────────────────────────────────────

export const BottomDrawerBackdrop = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.48);
  z-index: 1100;
  opacity: ${({ $open }) => ($open ? "1" : "0")};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition: opacity ${ANIMATION.duration.moderate} ease-out;
`;

export const BottomDrawer = styled.div<{ $open: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--color-background-2);
  border-top: 1px solid var(--color-card-border);
  border-radius: 20px 20px 0 0;
  z-index: 1101;
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + ${SPACING.sm}px);
  transform: translateY(${({ $open }) => ($open ? "0" : "100%")});
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  /* Delay hiding until the slide-out ends so closed items leave the tab order without cutting the animation. */
  transition:
    transform 280ms cubic-bezier(0.32, 0.72, 0, 1),
    visibility 0s linear ${({ $open }) => ($open ? "0s" : "280ms")};
  will-change: transform;
`;

export const BottomDrawerHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: var(--color-card-border);
  margin: ${SPACING.sm + SPACING.xs}px auto ${SPACING.xs}px;
`;

export const BottomDrawerSection = styled.div`
  padding: ${SPACING.xs}px ${SPACING.sm + SPACING.xs}px;
`;

export const BottomDrawerDivider = styled.div`
  height: 1px;
  background: var(--color-card-border);
  margin: ${SPACING.xs}px ${SPACING.lg}px;
`;

export const BottomDrawerNavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${SPACING.md}px;
  height: 52px;
  padding: 0 ${SPACING.sm + SPACING.xs}px;
  border-radius: 12px;
  text-decoration: none;
  color: var(--color-text-default);
  font-size: var(--text-base);
  font-weight: 400;
  transition: background ${ANIMATION.duration.fast} ease-out;
  width: 100%;

  @media (hover: hover) {
    &:hover {
      background: var(--color-nav-item-hover);
    }
  }

  &:active {
    background: var(--color-nav-item-hover-active);
  }
`;

export const BottomDrawerNavItemActive = styled(BottomDrawerNavItem)`
  color: var(--color-nav-bottom-accent);
  font-weight: 600;
`;

export const BottomDrawerDestructiveButton = styled(BaseButton)`
  display: flex;
  align-items: center;
  gap: ${SPACING.md}px;
  height: 52px;
  padding: 0 ${SPACING.sm + SPACING.xs}px;
  border-radius: 12px;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-error);
  font-size: var(--text-base);
  font-weight: 400;
  font-family: inherit;
  text-align: left;
  transition: background ${ANIMATION.duration.fast} ease-out;

  @media (hover: hover) {
    &:hover {
      background: var(--color-error-background);
    }
  }

  &:active {
    background: var(--color-error-background);
    filter: brightness(0.9);
  }
`;
